<template>
	<div>
		<StandardModal
			open
			:large="large"
			:small="small"
			class="selection-modal"
			:class="$attrs.class"
			:title="title"
			:confirmText="confirmText"
			:cancelText="cancelText"
			:confirmProps="{ disabled, ...confirmProps }"
			@body-resize="calcTableHeight"
			@confirm="$emit('confirm')"
			@cancel="onCancelChanges"
			@close="$attrs.onClose"
			ref="modal"
			:tabOrder="[
				{ css: '.filtering .pronto-input input', clickOnEnter: true },
				{ all: '.table-row .table-cell input', clickOnEnter: true, wait: 500 },
				{ css: '.amount-selected', clickOnEnter: true },
				{ css: '.clear-selection', clickOnEnter: true },
			]"
		>
			<template #title v-if="!title">
				<slot name="title" />
			</template>
			<div class="filtering" ref="filters">
				<slot name="filters" />
			</div>

			<Table
				:columns="columns"
				:rows="items"
				:selectedKeys="selectedKeys"
				:loadingMore="loadingMore"
				:loadingOverlay="loading"
				:disableAdditionalSelection="atMaxLimit"
				hideHeader
				clickable
				infinite
				selection
				selectOnRowClick
				@selection="onSelect"
				@load-more="$emit('load-more')"
				:height="tableHeight"
				loadMoreTrigger="200px"
				ref="mainTable"
			>
				<template v-slot:[slotName]="{ data, selected }">
					<slot name="cell" :data="data" :selected="selected" />
				</template>
			</Table>

			<template #footer-left v-if="!hideFooterContent">
				<div
					class="amount-selected"
					@click="amountSelectedClick"
					v-if="selectedCount > 0"
					v-text="
						$t('admin_groups_selected_members_button', {
							Count: selectedCount,
						})
					"
				/>
				<div
					class="clear-selection"
					@click="clearSelection"
					v-if="selectedCount > 0"
					v-t="'admin_groups_clear_selection_button'"
				/>
				<slot name="footer-max" v-if="atMaxLimit" />
				<slot name="footer-unselected" v-if="!selectedCount" />
			</template>
		</StandardModal>

		<StandardModal
			v-if="selectedCopyById"
			open
			:title="$t('admin_groups_current_selection_title')"
			:confirmText="$t('admin_people_add_to_groups_update_action')"
			:confirmProps="{ disabled: updateDisabled }"
			@confirm="updateSelection"
			@cancel="selectedCopyById = null"
			@body-resize="calcSelectionTableHeight"
		>
			<Table
				:columns="columns"
				:rows="selectedItems"
				:selectedKeys="selectedKeysCopy"
				clickable
				hideHeader
				selection
				selectOnRowClick
				@selection="onSelectCopy"
				:height="selectionTableHeight"
			>
				<template v-slot:[slotName]="{ data, selected }">
					<slot name="cell" :data="data" :selected="selected" />
				</template>
			</Table>
		</StandardModal>

		<StandardModal
			v-if="showDiscard"
			open
			small
			:title="$t('announcements_discard_changes')"
			:confirmText="$t('admin_common_discard_button_title')"
			:confirmProps="{ danger: true }"
			:cancelText="$t('admin_common_keep_editing_button_title')"
			@confirm="onDiscardConfirm"
			@cancel="showDiscard = false"
			@body-resize="calcSelectionTableHeight"
		>
			<div class="discard-wrapper">
				<TrashCanIcon halo />
				{{ $t('admin_common_discard_changes_confirm') }}
			</div>
		</StandardModal>
	</div>
</template>

<script>
import { StandardModal, Table } from '@/components/common'
import { TrashCanIcon } from '@/icons'

export default {
	components: {
		StandardModal,
		Table,
		TrashCanIcon,
	},
	inheritAttrs: false,
	emits: ['cancel', 'selection', 'prevented', 'confirm', 'load-more'],

	props: {
		items: Array,
		title: String,
		selectedItemsById: Object,
		confirmText: String,
		cancelText: String,
		confirmProps: Object,
		columns: Array,
		loading: Boolean,
		loadingMore: Boolean,
		sortItems: Function,
		skipDiscard: Boolean,
		allowEmptySelection: Boolean,
		large: {
			type: Boolean,
			default: true,
		},
		small: {
			type: Boolean,
			default: false,
		},
		hideFooterContent: {
			type: Boolean,
			default: false,
		},
		startingCount: {
			type: Number,
			default: 0,
		},
		maxLimit: {
			type: Number,
			default: 1000,
		},
	},

	data() {
		return {
			selectedCopyById: null,
			showDiscard: false,
			tableHeight: 571,
			selectionTableHeight: 600,
		}
	},

	computed: {
		selectedItems() {
			const items = Object.values(this.selectedItemsById)
				.filter((item) => item.selected === true)
				.map((item) => item.item)
			if (this.sortItems) {
				return items.sort(this.sortItems)
			}
			return items
		},

		selectedCount() {
			return Object.values(this.selectedItemsById).filter(
				(g) => g.selected === true
			).length
		},

		disabled() {
			if (this.allowEmptySelection) return false
			return this.selectedCount === 0
		},

		atMaxLimit() {
			return this.selectedCount + this.startingCount >= this.maxLimit
		},

		updateDisabled() {
			if (!this.selectedCopyById) return false
			return this.selectedItems.every(
				(g) => this.selectedCopyById[g.id].selected
			)
		},

		selectedKeys() {
			return Object.values(this.selectedItemsById).reduce(
				(acc, { item, selected }) => {
					acc[item.id] = selected
					return acc
				},
				{}
			)
		},

		selectedKeysCopy() {
			return Object.values(this.selectedCopyById).reduce(
				(acc, { item, selected }) => {
					acc[item.id] = selected
					return acc
				},
				{}
			)
		},

		slotName() {
			const field = this.columns?.[0]?.field ?? ''
			return `cell-${field}`
		},
	},

	methods: {
		amountSelectedClick() {
			this.selectedCopyById = Object.assign({}, this.selectedItemsById)
		},

		clearSelection() {
			this.$emit('selection', {})
		},

		onSelectCopy(item) {
			const selected = this.selectedCopyById[item.id]?.selected ?? false
			this.selectedCopyById[item.id] = { selected: !selected, item }
		},

		onSelect(item) {
			const selected = this.selectedItemsById[item.id]?.selected ?? false
			// if user attempts to select more above limit, prevent them from doing so
			if (!selected && this.atMaxLimit) return this.$emit('prevented', item)
			this.$emit('selectItem', item, !selected)
			// eslint-disable-next-line vue/no-mutating-props
			this.selectedItemsById[item.id] = { selected: !selected, item }
			this.$emit('selection', this.selectedItemsById)
		},

		updateSelection() {
			this.$emit('selection', this.selectedCopyById)
			this.selectedCopyById = null
		},

		calcTableHeight({ height }) {
			const borderSize = 1
			this.tableHeight = Math.floor(
				height - this.$refs.filters.clientHeight - borderSize
			)
		},

		calcSelectionTableHeight({ height }) {
			this.selectionTableHeight = Math.floor(height)
		},

		onCancelChanges() {
			if (this.selectedCount > 0 && !this.skipDiscard) {
				this.showDiscard = true
				return
			}
			this.$emit('cancel')
		},

		onDiscardConfirm() {
			this.selectedCopyById = null
			this.showDiscard = false
			this.$emit('cancel')
		},

		displayAlert(...args) {
			this.$refs.modal.$alert.error(...args)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.discard-wrapper {
	@include flex-column-center;
	max-width: 300px;
	margin: 24px auto;
	text-align: center;
	.pronto-icon {
		color: var(--red-200);
		font-size: 50px;
		margin-bottom: 24px;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

.standard-modal.selection-modal {
	.filtering {
		border-bottom: 1px solid var(--neutral-200);
		padding: 10px 20px;
	}

	.modal-footer-left {
		display: flex;
		font-family: Avenir;
		font-weight: 800;
		font-size: 15px;
		line-height: 20px;
		color: var(--blue-300);

		.amount-selected {
			padding-right: 10px;
			border-right: 1px solid var(--neutral-200);
			cursor: pointer;
		}

		.clear-selection {
			padding-left: 10px;
			cursor: pointer;
		}
	}
}

@include overrideScheme('dark') {
	.selection-modal {
		.filtering {
			border-color: var(--neutral-300);
		}
	}
}
</style>
